import { LISTSELECTION_TYPE, useStore } from '@store/appStore';
import { useEffect, useMemo } from 'react';
import { shallow } from 'zustand/shallow';
import StudentThumbnail from '@components/StudentThumbnail';
import PlaceholderPerson from '@components/PlaceholderPerson';
import SkeletonAnim from '@components/Animation/SkeletonAnim';
import { CourseCell, ListItemContainer, NameContainer, PersonData, Picture, Schedule, StatusRow } from './ListItem.styled';
import { ListThemeColors, ListThemeContrast } from '@styles/themes';
import { AttendanceMap } from '@models/AttendanceType';
import ListAttendanceIndicator from '@components/ListAttendanceIndicator';
import { normalizeName } from '@utils/textTools';
import { useSearch } from '@providers/SearchProvider';
import { Shadow } from '@styles/reusable';
import { ViewColoring, ViewIndicators } from '@models/Accessibility';
import persistentStore from '@store/persistentStore';
import HaveNote from '@components/Buttons/HaveNote';
import useVisibility from '@hooks/useVisibility';
import RegMethod from '@components/Buttons/RegistrationMethod';
import StatusBar from '@components/Buttons/StatusBar';
import Tooltip from '@components/Tooltips/Tooltip/Tooltip';
import Status_Icon from '@assets/svg/status.svg';
import styled from '@emotion/styled';

interface IListItemProps {
  content: any;
  className?: string;
  index: number;
}

const StatusContent = styled.div<{show?:boolean}>`
  display: ${props => props.show?"block":"none"};
  height:100%;
  aspect-ratio: 0.8 / 1;
  z-index: 10;

  > div {
    height:100%;
    display: flex;
    justify-content: center;
    align-items: center;
  }
  
`;

export const ListItem = ({ content, className, index = 0 }: IListItemProps) => {
  const [isVisible, ref] = useVisibility<HTMLDivElement>(-30);
  const handleCheckboxChange = () => {};
  const { username, lastname, firstname, sis_id, notePresent, noteUnreadCount, initial_Method } = content;
  const { schedule, listSelection, updateLastSelection, massUpdateListSelectionStudent } = useStore(
    (state) => ({
      schedule: state.schedule,
      listSelection: state.listSelection,
      updateLastSelection: state.updateLastSelection,
      massUpdateListSelectionStudent: state.massUpdateListSelectionStudent,
    }),
    shallow,
  );
  const { registrationQuery } = useSearch();
  const { viewColoring, viewIndicators } = persistentStore();
  const onStudentSelect = () => {
    updateLastSelection(LISTSELECTION_TYPE.STUDENT, index);
    massUpdateListSelectionStudent(sis_id);
  };
  const normalizedName = normalizeName(`${firstname} ${lastname}`, 50);
  const mappedSchedule = useMemo(() => {
    if (content?.reservations?.length > 0 && schedule.data?.length > 0) {
      const newSchedule = schedule.data?.map((item) => {
        const reservationIndex = content.reservations?.findIndex((reservation) => reservation.id === item.id);
        if (reservationIndex !== -1) {
          const data = content.reservations[reservationIndex];
          return {
            id: data.id,
            type: data.type,
            initial_Method: data.initial_Method,
            tag_ids: data.tag_ids
          };
        }
        return null;
      });
      return newSchedule;
    }
  }, [schedule.data, content.reservations]);

  const hasSelection = useMemo(() => {
    const anySelected = Object.values(listSelection.data).some((student: any) => Object.values(student.reservations).some(Boolean));
    if (!anySelected) {
      return true;
    }
    // console.log(listSelection.data);
    return Object.values(listSelection.data[index].reservations).some(Boolean);
  }, [listSelection]);

  useEffect(() => {
    if (hasSelection && registrationQuery?.forType === 'STUDENT' && registrationQuery.forVal === username) {
      window.scrollTo(0, index * (44 + 10));
    }
  }, []);

  return (
    <ListItemContainer
      active={hasSelection}
      className={className}
      disabled={registrationQuery?.forType === 'student' ? true : false}
      ref={ref}
    >
      {/* <Tooltip content={i18n.t('admin-enddate')} delayEnter={500} offsetY={-4} placement="top-center"> */}
      <NameContainer onClick={onStudentSelect}>
        {/* <Checkbox checked={checked} onChange={handleCheckboxChange} /> */}
        <Picture>
          <StudentThumbnail font={"1.4em"} name={firstname + " " + (lastname && normalizeName(lastname))} src={username} />
        </Picture>
        <PersonData>{`${firstname} ${lastname}`}</PersonData>
        <StatusBar note={{fullDayView:true, id: sis_id, show:notePresent, notecount:noteUnreadCount}} />
      </NameContainer>
      {/* </Tooltip> */}

      <Schedule cols={schedule.data?.length}>
        {/* {mappedSchedule?.map((item, i) => (item ? <CourseItem studentId={sis_id} key={i} content={item} /> : <div key={i}></div>))} */}
        {mappedSchedule?.map((item, i) =>
          item ? (
            <CourseItem
              username={username}
              indicator={viewIndicators}
              view={viewColoring === ViewColoring.CONTRAST}
              isContrast={viewColoring === ViewColoring.CONTRAST || viewIndicators === ViewIndicators.COLOR_AND_CONTRAST_ICONS}
              studentId={sis_id}
              key={i}
              index={index}
              content={item}
            />
          ) : (
            <div key={i}></div>
          ),
        )}
      </Schedule>
    </ListItemContainer>
  );
};

interface CourseProps {
  content: any;
  index: number;
  studentId: string;
  isContrast: boolean;
  view?: boolean;
  indicator?: ViewIndicators;
  reason?:any
  username:string;
}

const CourseItem = (props: CourseProps) => {
  const { type, initial_Method, reason } = props.content || null;
  const { listSelection, updateListSelection, systemSettings, wondeTags, settings } = useStore(
    (state) => ({
      listSelection: state.listSelection,
      updateListSelection: state.updateListSelection,
      systemSettings: state.systemSettings,
      wondeTags: state.wondeTags,
      settings: state.settings
    }),
    shallow,
  );

  const active = useMemo(() => {
    if (listSelection.data[props.index]?.reservations[props.content.id]) {
      return listSelection.data[props.index]?.reservations[props.content.id];
    }
    return false;
  }, [listSelection.data]);

  const handleCourseCellClick = () => {
    updateListSelection(props.content.id, props.studentId);
  };

  const getTag = () => {
    let tags = []

    if (props.content?.type === 1) {                
      tags = wondeTags?.present.map(t => {
          return {tag_id: t.id, name: "(" + t.code + ") " + (t.short_name?t.short_name:t.description), html_color: "2A848E"}
      })
      tags = [{tag_id: -1, name: "Present", html_color: "2A848E"}].concat(tags)
      }
      else if(props.content?.type === 2){
        tags = wondeTags?.absent.map(t => {
              return {tag_id: t.id, name: "(" + t.code + ") " + (t.short_name?t.short_name:t.description), html_color: "fb4856"}
          })
      }
      else if(props.content?.type === 3){                
        tags = wondeTags?.late.map(t => {
              return {tag_id: t.id, name: "(" + t.code + ") " + (t.short_name?t.short_name:t.description), html_color: "ffd112"}
          })
      }


        let t = tags.find(t => t?.tag_id == props.content?.tag_ids)
        if(t)return t.name        
        else return ""
    }  

  let defaultAttendanceSetting = systemSettings.find((s) => s.APP_Setting_ID === 19);
  const status = defaultAttendanceSetting && defaultAttendanceSetting.value === 'Present' && type === -1 ? 1 : type;
  const coloring = props.view ? ListThemeContrast[AttendanceMap[status]] : ListThemeColors[AttendanceMap[status] || 'default'];

  return (
    <CourseCell
      // @ts-ignore
      css={coloring}
      active={active}
      onClick={handleCourseCellClick}
      backgroundColor={props.indicator === ViewIndicators.ICONS}
    >

      <StatusRow>
        <RegMethod
          studentUsername={props.username}
          studentId={props.studentId}
          reservationId={props.content.id}
          fullDayView={true}
          show={initial_Method !== null && initial_Method !== 0 && initial_Method !== undefined}
          registrationMethod={initial_Method}
          />

        {settings.USEWONDE && <StatusContent show={props.content?.tag_ids !== null}>
          <Tooltip content={getTag()}>
            <Status_Icon />
          </Tooltip>
        </StatusContent>}
        <ListAttendanceIndicator indicator={props.indicator !== ViewIndicators.COLOR} isContrast={props.isContrast} type={status} />
      </StatusRow>
    </CourseCell>
  );
};

export const MockListItem = ({ style }) => {
  const { schedule } = useStore(
    (state) => ({
      schedule: state.schedule,
    }),
    shallow,
  );

  return (
    <ListItemContainer style={{ ...style, pointer: 'not-allowed' }} active={true}>
      <NameContainer mock={true}>
        <Picture>
          <PlaceholderPerson />
        </Picture>
        <PersonData>
          <SkeletonAnim height={15} width={50} />
          <SkeletonAnim height={15} width={150} />
        </PersonData>
      </NameContainer>
      <Schedule cols={schedule.data?.length}>
        {schedule.data?.map((item, i) => (
          <CourseCell key={i} active={false}>
            {' '}
          </CourseCell>
        ))}
      </Schedule>
    </ListItemContainer>
  );
};
